import React, { useEffect, useState } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useIntl, FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";

import "./LoadTemplateDialog.scss";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import { duplicateQuickRequest, duplicateQuoteRequest, getShipmentTemplates, deleteShipmentTemplate } from "api";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Alert from "components/molecules/Alert/Alert";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import { modifyDraftData } from "helpers/requestFormHelper";
import usePermissions from "hooks/usePermissions";
import { trackQuoteTemplateLoaded as trackQuoteTemplateLoadedV1 } from "pages/shipper/RequestForm/formEvents/requestFormAnalytics";
import { trackQuoteTemplateLoaded as trackQuoteTemplateLoadedV2 } from "@RequestFormV2/formEvents/requestFormAnalytics";
import PickupDeliveryTime from "./PickupDeliveryTime";
import ProviderTimingForm from "../Provider/ProviderTimingForm";
import SearchSelect from "../Common/SearchSelect";
import { applyModalSchedulePlanningToStops } from "./applyModalSchedulePlanningToStops";
import { buildLoadTemplateModalSchedule } from "./buildLoadTemplateModalSchedule";

type loadTemplateProps = {
  isOpen: boolean;
  close: () => void;
  isQuoteRequest: boolean;
  isNewShipment?: boolean;
  formVersion?: "v1" | "v2";
  transformLoadedData?: (v1Draft: Record<string, unknown>) => Record<string, unknown>;
};

interface QuoteDataProps {
  [key: string]: any;
}

const LoadTemplateDialog: React.FC<loadTemplateProps> = function ({
  isOpen,
  close,
  isQuoteRequest,
  isNewShipment,
  formVersion,
  transformLoadedData,
}) {
  const intl = useIntl();
  const requestFormMethods = useFormContext();
  const methods = useForm({
    shouldFocusError: true,
    mode: "onChange",
  });
  const { handleSubmit, getValues, reset, setValue, watch } = methods;
  const [quoteData, setQuoteData] = useState<QuoteDataProps>({});
  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};
  const templateId = searchParams.get("template_id");
  watch();

  const duplicateQuote = () => {
    setIsLoading(true);
    if (!getValues("template_name")) return;
    const duplicateFunc = !isQuoteRequest ? duplicateQuickRequest : duplicateQuoteRequest;
    duplicateFunc(getValues("template_name"))
      .then((res) => {
        setQuoteData(res.data);
        // Prefill modal planning from template (prefer stops[] types/dates)
        reset({
          template_name: getValues("template_name"),
          schedule: buildLoadTemplateModalSchedule(res.data?.data?.schedule),
        });
        setIsLoading(false);
      })
      .catch((err) => errorToast(err));
  };

  const dropdownOptions = templates.map((template: { template_id: string; reference_number: string; label: string }) => ({
    id: template.template_id,
    value: template.reference_number,
    label: template.label,
  }));

  const onSubmit = (data: { [key: string]: any }) => {
    const existingData = { ...quoteData.data };
    const updatedData = {
      ...existingData,
      schedule: applyModalSchedulePlanningToStops({
        ...existingData.schedule,
        ...data.schedule,
      }),
      ...(isQuoteRequest && {
        shipment: {
          ...existingData.shipment,
          ...data.shipment,
        },
      }),
    };
    const v1Draft = modifyDraftData(updatedData, false, false, enable_new_address_structure);
    searchParams.set("draft_id", quoteData.reference_number);
    searchParams.delete("template_id");
    setSearchParams(searchParams);

    if (transformLoadedData) {
      requestFormMethods.reset(transformLoadedData(v1Draft));
    } else {
      requestFormMethods.setValue("organizational.location", updatedData.organizational.location);
      requestFormMethods.reset(v1Draft);
    }
    const loadedTemplateId = templates.find(
      (template) => template.reference_number === getValues("template_name"),
    )?.template_id;
    if (loadedTemplateId) {
      if (formVersion === "v2") {
        trackQuoteTemplateLoadedV2(String(loadedTemplateId));
      } else if (formVersion === "v1" && isQuoteRequest) {
        trackQuoteTemplateLoadedV1(String(loadedTemplateId));
      }
    }
    close();
  };

  const loadShipmentTemplates = () => {
    getShipmentTemplates()
      .then((res) => {
        if (!isQuoteRequest) setTemplates(res.data.quick_request_templates);
        else setTemplates(res.data.quote_request_templates);
        setIsLoadingTemplates(false);
      })
      .catch((err) => errorToast(err));
  };

  const handleDeleteTemplate = () => {
    setIsDeleting(true);
    const templateId = templates.find((template) => template.reference_number === getValues("template_name"))?.template_id;
    if (!templateId)
      errorToast(
        intl.formatMessage({
          id: "tasks.taskDialog.errorMessage",
          defaultMessage: "Something went wrong, try again in a few minutes.",
        }),
      );
    deleteShipmentTemplate(templateId).then((res) => {
      if (res.status === 204) {
        toast(
          intl.formatMessage({
            id: "components.molecules.navigation.deleteTemplateSuccess",
            defaultMessage: "Template deleted successfully",
          }),
          {
            type: "success",
          },
        );
        setQuoteData({});
        loadShipmentTemplates();
        setShowConfirmDelete(false);
        setIsDeleting(false);
      }
    });
    setValue("template_name", "");
  };

  useEffect(() => {
    if (isOpen) {
      loadShipmentTemplates();
      if (templateId) setValue("template_name", templateId);
    }
    return () => {
      reset();
      setShowConfirmDelete(false);
      setQuoteData({});
    };
  }, [isOpen]);

  useEffect(() => {
    if (getValues("template_name")) duplicateQuote();
  }, [getValues("template_name")]);

  return (
    <Dialog
      size="lg"
      show={isOpen}
      title={intl.formatMessage({
        id: "quote-request.loadTemplate",
        defaultMessage: "Load Template",
      })}
      onHide={close}
      className="template-dialog-wrapper"
    >
      <FormProvider {...methods}>
        <form>
          <div className="mb-3">
            {isLoadingTemplates && <LoadingSkeleton rows={1} noShadow />}
            {templates.length > 0 && (
              <Row>
                <Col lg={2} md={3}>
                  <p className="template-section-title">
                    <FormattedMessage id="quote-request.template" defaultMessage="Template" />
                  </p>
                </Col>
                <Col lg={4} md={6}>
                  <SearchSelect
                    options={dropdownOptions}
                    name="template_name"
                    label={intl.formatMessage({
                      id: "new-request.form.chooseTemplate",
                      defaultMessage: "Choose template",
                    })}
                    placeholder={intl.formatMessage({
                      id: "new-request.form.chooseTemplate",
                      defaultMessage: "Choose template",
                    })}
                  />
                </Col>
              </Row>
            )}

            {!isLoadingTemplates && templates.length === 0 && (
              <div className="text-center">
                <p className="mb-0">
                  {intl.formatMessage({
                    id: "new-request.form.noTemplates",
                    defaultMessage: "No templates available",
                  })}
                </p>
              </div>
            )}
          </div>
          {isLoading && <LoadingSkeleton rows={3} noShadow />}
          {Object.keys(quoteData).length > 0 && !isLoading && (
            <>
              <hr />
              <PickupDeliveryTime location={quoteData.data.organizational.location} />

              {isQuoteRequest && !isNewShipment && (
                <Row>
                  <Col lg={2} md={2}>
                    <p className="template-section-title">
                      <FormattedMessage id="new-request.timing.timing" defaultMessage="Timing" />
                    </p>
                  </Col>
                  <Col lg={10} md={10}>
                    <ProviderTimingForm />
                  </Col>
                </Row>
              )}
            </>
          )}
          {!showConfirmDelete && (
            <div className="d-flex mt-4">
              <div className="mr-auto">
                {getValues("template_name") && !showConfirmDelete && (
                  <Button
                    className="rounded-pill"
                    variant="danger"
                    onClick={() => setShowConfirmDelete(true)}
                    label={intl.formatMessage({
                      id: "shipment-quotes.components.template.deleteTemplate",
                      defaultMessage: "Delete template",
                    })}
                  />
                )}
              </div>
              <div className="d-flex justify-content-end">
                <Button
                  variant="outline-primary"
                  className="btn mr-2 rounded-pill"
                  onClick={close}
                  label={intl.formatMessage({
                    id: "general.cancel",
                    defaultMessage: "Cancel",
                  })}
                />
                <Button
                  disabled={!getValues("template_name")}
                  type="button"
                  onClick={handleSubmit((data) => onSubmit(data))}
                  className="btn btn-primary rounded-pill "
                  label={intl.formatMessage({
                    id: "general.confirm",
                    defaultMessage: "Confirm",
                  })}
                  data-testid={testIds.FORM_CONFIRM_LOAD_TEMPLATE_BUTTON}
                />
              </div>
              <div />
            </div>
          )}
          {showConfirmDelete && (
            <div className="d-flex mt-4 align-items-center justify-content-between">
              <Alert variant="warning" className="">
                <FormattedMessage
                  id="shipment-quotes.components.template.deleteTemplateConfirm"
                  defaultMessage="Are you sure you want to delete this template?"
                />
              </Alert>

              <span className="d-flex">
                <Button
                  className="ml-3 rounded-pill"
                  variant="outline-primary"
                  label={intl.formatMessage({
                    id: "general.cancel",
                    defaultMessage: "Cancel",
                  })}
                  onClick={() => setShowConfirmDelete(false)}
                />
                <Button
                  className="ml-3 rounded-pill"
                  label={intl.formatMessage({
                    id: "general.delete",
                    defaultMessage: "Delete",
                  })}
                  variant="danger"
                  isLoading={isDeleting}
                  onClick={handleDeleteTemplate}
                  data-testid={testIds.FORM_DELETE_TEMPLATE_BUTTON}
                />
              </span>
            </div>
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
};

LoadTemplateDialog.defaultProps = {
  isNewShipment: false,
  formVersion: undefined,
  transformLoadedData: undefined,
};

export default LoadTemplateDialog;
